import type {
  AppSettings,
  DataHealth,
  DeveloperHealthSummary,
  DeveloperRunHistory,
  ChangePasswordRequest,
  DashboardLimits,
  DashboardSummary,
  DiscoveredEntities,
  BudgetStatus,
  PriceFeedStatus,
  EfficiencySummary,
  FilterOptions,
  ExecutiveSummary,
  LoginResponse,
  OverviewSummary,
  RtkDetailSummary,
  SettingsHistoryResponse,
  SettingsHistorySection,
  SettingsSection,
  SessionsSummary,
  SpendSummary,
  Tool,
  TokenOptimizationSummary,
} from '@ace-tracker/shared';
import { clearStoredToken, getStoredToken } from './auth';
import type { CostFilters } from './urlState';

export class UnauthorizedError extends Error {}

async function authedFetch(url: string, init: { method?: string; body?: unknown } = {}): Promise<Response> {
  const token = getStoredToken();
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(url, {
    method: init.method,
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
    // Revalidate instead of reusing the browser's HTTP cache (the API sends
    // max-age=60), so figures reflect a Settings change as soon as React
    // Query refetches. The API's own query cache still absorbs the cost.
    cache: 'no-cache',
  });
  if (res.status === 401) {
    clearStoredToken();
    throw new UnauthorizedError('Session expired, please sign in again');
  }
  return res;
}

/** Server-side limits (data retention) that bound which time ranges are offered. */
export async function fetchDashboardLimits(): Promise<DashboardLimits> {
  const res = await authedFetch('/api/dashboard/limits');
  if (!res.ok) {
    throw new Error(`Dashboard limits request failed: ${res.status}`);
  }
  return res.json() as Promise<DashboardLimits>;
}

export async function fetchDashboardSummary(range?: {
  from?: string;
  to?: string;
  developerId?: string;
  developerIds?: string[];
  tool?: Tool;
}): Promise<DashboardSummary> {
  const params = new URLSearchParams();
  if (range?.from) params.set('from', range.from);
  if (range?.to) params.set('to', range.to);
  if (range?.developerId) params.set('developerId', range.developerId);
  for (const id of range?.developerIds ?? []) params.append('developerIds', id);
  if (range?.tool) params.set('tool', range.tool);

  const res = await authedFetch(`/api/dashboard/summary?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Dashboard summary request failed: ${res.status}`);
  }
  return res.json() as Promise<DashboardSummary>;
}

/** Fetches the "All tools" cross-tool comparison — see dashboard/AGENTS.md "API rules". */
export async function fetchOverviewSummary(range?: { from?: string; to?: string }): Promise<OverviewSummary> {
  const params = new URLSearchParams();
  if (range?.from) params.set('from', range.from);
  if (range?.to) params.set('to', range.to);

  const res = await authedFetch(`/api/dashboard/overview?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Dashboard overview request failed: ${res.status}`);
  }
  return res.json() as Promise<OverviewSummary>;
}

/** Query string for the Spend, Efficiency and Sessions views: range plus their record filters. */
function costViewParams(range: { from?: string; to?: string }, filters?: CostFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (range.from) params.set('from', range.from);
  if (range.to) params.set('to', range.to);
  for (const [key, values] of Object.entries(filters ?? {})) for (const value of values) params.append(key, value);
  return params;
}

/** Fetches the Spend view: indicative cost by day, tool, model, token type, project, and session. */
export async function fetchSpendSummary(range: { from?: string; to?: string }, filters?: CostFilters): Promise<SpendSummary> {
  const res = await authedFetch(`/api/dashboard/spend?${costViewParams(range, filters).toString()}`);
  if (!res.ok) {
    throw new Error(`Spend request failed: ${res.status}`);
  }
  return res.json() as Promise<SpendSummary>;
}

/** Budget for the current UTC month; ignores the date range and filters. */
export async function fetchBudgetStatus(): Promise<BudgetStatus> {
  const res = await authedFetch('/api/dashboard/budget');
  if (!res.ok) throw new Error(`Budget request failed: ${res.status}`);
  return res.json() as Promise<BudgetStatus>;
}

/** Fetches the Efficiency view: Claude Code and Codex efficiency ratios by day, model, and team. */
export async function fetchEfficiencySummary(range: { from?: string; to?: string }, filters?: CostFilters): Promise<EfficiencySummary> {
  const res = await authedFetch(`/api/dashboard/efficiency?${costViewParams(range, filters).toString()}`);
  if (!res.ok) {
    throw new Error(`Efficiency request failed: ${res.status}`);
  }
  return res.json() as Promise<EfficiencySummary>;
}

/** Fetches the Sessions view: per-session cost, duration, and requests with runaway flags. */
export async function fetchSessionsSummary(range: { from?: string; to?: string }, filters?: CostFilters): Promise<SessionsSummary> {
  const res = await authedFetch(`/api/dashboard/sessions?${costViewParams(range, filters).toString()}`);
  if (!res.ok) throw new Error(`Sessions request failed: ${res.status}`);
  return res.json() as Promise<SessionsSummary>;
}

/** Models, projects, and surfaces the cost-view filters can offer for a range. */
export async function fetchFilterOptions(range: { from?: string; to?: string }): Promise<FilterOptions> {
  const res = await authedFetch(`/api/dashboard/filter-options?${costViewParams(range).toString()}`);
  if (!res.ok) throw new Error(`Filter options request failed: ${res.status}`);
  return res.json() as Promise<FilterOptions>;
}

/** Fetches token-optimization estimates from the RTK collector. */
export async function fetchTokenOptimizationSummary(range?: { from?: string; to?: string; developerId?: string; developerIds?: string[] }): Promise<TokenOptimizationSummary> {
  const params = new URLSearchParams();
  if (range?.from) params.set('from', range.from);
  if (range?.to) params.set('to', range.to);
  if (range?.developerId) params.set('developerId', range.developerId);
  for (const id of range?.developerIds ?? []) params.append('developerIds', id);

  const res = await authedFetch(`/api/dashboard/optimization?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Token optimization request failed: ${res.status}`);
  }
  return res.json() as Promise<TokenOptimizationSummary>;
}

/** Fetches the dedicated RTK view: daily/weekly/monthly history, lifetime totals, parse recovery, command families. */
export async function fetchRtkDetailSummary(range?: { from?: string; to?: string; developerId?: string; developerIds?: string[] }): Promise<RtkDetailSummary> {
  const params = new URLSearchParams();
  if (range?.from) params.set('from', range.from);
  if (range?.to) params.set('to', range.to);
  if (range?.developerId) params.set('developerId', range.developerId);
  for (const id of range?.developerIds ?? []) params.append('developerIds', id);

  const res = await authedFetch(`/api/dashboard/optimization/rtk?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`RTK request failed: ${res.status}`);
  }
  return res.json() as Promise<RtkDetailSummary>;
}

/** Data health for a range: per-source coverage, freshness, and collector run outcomes. */
export async function fetchDataHealth(range: { from: string; to: string }): Promise<DataHealth> {
  const params = new URLSearchParams({ from: range.from, to: range.to });
  const res = await authedFetch(`/api/dashboard/health?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Data health request failed: ${res.status}`);
  }
  return res.json() as Promise<DataHealth>;
}

/** Developer-level collector health: when each developer's collector last ran and sent data. */
export async function fetchDeveloperHealth(): Promise<DeveloperHealthSummary> {
  const res = await authedFetch('/api/dashboard/health/developers');
  if (!res.ok) throw new Error(`Developer health request failed: ${res.status}`);
  return res.json() as Promise<DeveloperHealthSummary>;
}

/** One developer's collector executions in a range, newest first. */
export async function fetchDeveloperRunHistory(developerId: string, range: { from: string; to: string }): Promise<DeveloperRunHistory> {
  const params = new URLSearchParams({ from: range.from, to: range.to, developerId });
  const res = await authedFetch(`/api/dashboard/health/developers/runs?${params.toString()}`);
  if (!res.ok) throw new Error(`Run history request failed: ${res.status}`);
  return res.json() as Promise<DeveloperRunHistory>;
}

/** Fetches the Executive view's leadership summary (current vs previous period). */
export async function fetchExecutiveSummary(range?: { from?: string; to?: string }): Promise<ExecutiveSummary> {
  const params = new URLSearchParams();
  if (range?.from) params.set('from', range.from);
  if (range?.to) params.set('to', range.to);

  const res = await authedFetch(`/api/dashboard/executive?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Executive summary request failed: ${res.status}`);
  }
  return res.json() as Promise<ExecutiveSummary>;
}

export async function fetchSettings(): Promise<AppSettings> {
  const res = await authedFetch('/api/settings');
  if (!res.ok) throw new Error(`Settings request failed: ${res.status}`);
  return res.json() as Promise<AppSettings>;
}

/** Replaces one settings section; resolves with the full saved settings. */
export async function saveSettingsSection<S extends SettingsSection>(section: S, value: AppSettings[S]): Promise<AppSettings> {
  const res = await authedFetch(`/api/settings/${section}`, { method: 'PUT', body: { [section]: value } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const issue = (body as { issues?: { message?: string }[] }).issues?.[0]?.message;
    throw new Error(issue ?? (body as { error?: string }).error ?? `Saving settings failed: ${res.status}`);
  }
  return res.json() as Promise<AppSettings>;
}

/** Settings → History: saved changes, newest first. */
export async function fetchSettingsHistory(query: { section?: SettingsHistorySection; limit: number }): Promise<SettingsHistoryResponse> {
  const params = new URLSearchParams({ limit: String(query.limit) });
  if (query.section) params.set('section', query.section);
  const res = await authedFetch(`/api/settings/history?${params.toString()}`);
  if (!res.ok) throw new Error(`History request failed: ${res.status}`);
  return res.json() as Promise<SettingsHistoryResponse>;
}

/** Developer and model ids seen in telemetry recently — for the Settings page. */
export async function fetchDiscoveredEntities(): Promise<DiscoveredEntities> {
  const res = await authedFetch('/api/settings/discovered');
  if (!res.ok) throw new Error(`Discovery request failed: ${res.status}`);
  return res.json() as Promise<DiscoveredEntities>;
}

function priceFeedParams(models: string[]): string {
  const params = new URLSearchParams();
  for (const model of models) params.append('models', model);
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** Settings → Model pricing: the public list-price feed compared with the pricing rows. `models` adds unpriced ids. */
export async function fetchPriceFeedStatus(models: string[], refresh = false): Promise<PriceFeedStatus> {
  const url = `/api/settings/price-feed${refresh ? '/refresh' : ''}${priceFeedParams(models)}`;
  const res = await authedFetch(url, refresh ? { method: 'POST' } : undefined);
  if (!res.ok) throw new Error(`Price feed request failed: ${res.status}`);
  return res.json() as Promise<PriceFeedStatus>;
}

/** Replaces the chosen models' rows with the feed's dated list prices; resolves with the saved settings. */
export async function applyPriceFeed(models?: string[]): Promise<{ settings: AppSettings; updated: string[] }> {
  const res = await authedFetch('/api/settings/price-feed/apply', { method: 'POST', body: models ? { models } : {} });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Applying the price feed failed: ${res.status}`);
  }
  return res.json() as Promise<{ settings: AppSettings; updated: string[] }>;
}

/** Settings → Account. Resolves with a fresh session — the old token stops working once the password changes. */
export async function changePassword(request: ChangePasswordRequest): Promise<LoginResponse> {
  const token = getStoredToken();
  // Not authedFetch: a 403 here means "wrong current password", not an expired session.
  const res = await fetch('/api/auth/change-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(request),
  });
  if (res.status === 401) {
    clearStoredToken();
    throw new UnauthorizedError('Session expired, please sign in again');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; issues?: { message?: string }[] };
    throw new Error(body.issues?.[0]?.message ?? body.error ?? `Changing the password failed: ${res.status}`);
  }
  return res.json() as Promise<LoginResponse>;
}
